import React from 'react';
import { hot } from 'react-hot-loader';
import Link from 'redux-first-router-link';
import { difference, intersect } from '../../utils/array';
import { translateObjectDefaultData, removeLanguagePrefix } from '../../utils/locale';
import { urlify, translate, prefix, convertToLatin } from '../../utils/string';
import s from './Navigation.scss';

const Navigation = ({ navigation, brands, brandList = {}, setup, menuPopupHeight, productCache = {}, openMenuHandler, expandMenuHandler, resetFilterHandler }) => (
	<nav className={s.navigation}>
		<Link className={s.navigation_miniLogo} to={prefix(setup, '')} aria-labelledby="Go to home">
			<svg xmlns="http://www.w3.org/2000/svg" width="30" height="26" viewBox="0 0 30 26">
				<path d="M11,15 L18,15 L14.5002365,6 L11,15 Z M19.8547389,19.5160405 L10.008527,19.5160405 L7.99147299,25 L3,25 L12.2992756,1 L17.7693178,1 L27,25 L21.8720193,25 L19.8547389,19.5160405 Z" />
			</svg>
		</Link>
		<a className={[s.navigation_mobileMenu, ...(navigation.open ? [s.navigation_mobileMenu__open] : [])].join(' ')} href="#open-mobile-menu" title="Open mobile menu" onClick={openMenuHandler} aria-labelledby="Open mobile menu"><span /><span /><span /></a>
		<ul className={s.level1} style={{ maxHeight: `${menuPopupHeight}px` }}>
			{
				navigation.header.map((level1, i) => {
					const level1URI = translate(setup, level1, 'uri');
					const combinedURILevel1 = prefix(setup, level1URI);
					const brandColumns = [];
					const level2SubCategoryLinks = [];
					const level2Links = [];
					const isBrandsList = brands?.posts?.[0]?.meta && translateObjectDefaultData(setup, brands.posts[0].meta).fullUri === level1URI;
					const level1Styles = level1.styles || null;

					if (level1.subPages) {
						// Get column with most links
						const dropdownCSS = {};
						const maxTotalLinks = level1.subPages.reduce((acc, val) => (val.subPages ? [...acc, (val.subPages.length + 1)] : acc), []).sort((a, b) => b - a);
						let listHeight = maxTotalLinks.length > 0 && maxTotalLinks[0] > 7 ? maxTotalLinks[0] / 2 : maxTotalLinks[0];
						if (listHeight < maxTotalLinks.slice(-1)) listHeight = maxTotalLinks.slice(-1);
						if (listHeight < 8) listHeight = 8;
						if (listHeight) dropdownCSS.height = Math.ceil(listHeight) * 22;

						level1.subPages.forEach((level2) => {
							const level2URI = prefix(setup, removeLanguagePrefix(translate(setup, level2, 'uri')));
							const level2Styles = level2.styles || null;

							if (level2.subPages) {
								level2SubCategoryLinks.push(
									<div key={urlify(level2URI)} className={[s.level2_block, ...(level2.subPages.length > 9 ? [s.level2_block__doubleColumn] : [])].join(' ')} style={dropdownCSS}>
										<div className={s.level2_block_item}>
											<Link onClick={resetFilterHandler} className={[s.level2_block_item_link, 'a-level2Link'].join(' ')} to={level2URI} style={level2Styles}>
												<span className={[s.level2_block_item_link_underlined, 'a-level2Link'].join(' ')}>{translate(setup, level2, 'title')}</span>
											</Link>
										</div>
										{
											level2.subPages.map((level3) => {
												const level3URI = prefix(setup, translate(setup, level3, 'uri'));
												const level3Styles = level3.styles || null;

												return (
													<div className={s.level2_block_item} key={urlify(level3URI)}>
														<Link onClick={resetFilterHandler} className={[s.level2_block_item_link, 'a-level3Link'].join(' ')} to={level3URI} style={level3Styles}>
															<span className="a-level3Link">{translate(setup, level3, 'title')}</span>
														</Link>
													</div>
												);
											})
										}
									</div>,
								);
							} else {
								level2Links.push(
									<div className={s.level2_block_item} key={urlify(level2URI)}>
										<Link onClick={resetFilterHandler} className={[s.level2_block_item_link, 'a-level2Link'].join(' ')} to={level2URI}>
											<span className="a-level2Link">{translate(setup, level2, 'title')}</span>
										</Link>
									</div>,
								);
							}
						});
					}

					if (brandList.content && isBrandsList) {
						const perColumn = Math.ceil(brandList.content.length / 6);
						let column = 0;
						let oldLetter = '';
						brandList.content.filter((b) => (
							!b.disabled
						)).sort((a, b) => (
							translate(setup, a.title).toLowerCase().localeCompare(translate(setup, b.title).toLowerCase())
						)).forEach((brand, j) => {
							const letter = convertToLatin(translate(setup, brand.uri).toLowerCase().slice(0, 1));

							// if not the same letter and column limit is maxed
							if (letter !== oldLetter && j > perColumn * (column + 1)) {
								// Add the letter before from the last letter of the previous column.
								const lastLetter = String.fromCharCode(letter.charCodeAt() - 1);
								if (brandColumns[column].letters.indexOf(lastLetter) === -1) brandColumns[column].letters.push(lastLetter);
								column += 1;
							}

							if (!brandColumns[column]) brandColumns[column] = { letters: [letter], brands: [] };
							if (brandColumns[column].letters.indexOf(letter) === -1) brandColumns[column].letters.push(letter);
							brandColumns[column].brands.push(brand);

							oldLetter = letter;
						});
					}

					const level1Click = {};
					let expandable = null;
					if (level1.subPages) {
						level1Click.onClick = expandMenuHandler;
						expandable = <span className={s.level1_column_link_expandIndicator} />;
					} else {
						level1Click.onClick = resetFilterHandler;
					}

					const hasCampaigns = {};
					const { localized = {}, filterArrays = {} } = productCache?.[setup.market]?.[setup.pricelist] || {};
					const { newProducts = false, productsOnSale = false, categories: rawCategories = {} } = filterArrays;
					const isLocalized = localized[setup.language];
					const categories = isLocalized ? Object.keys(rawCategories).reduce((acc, category) => {
						const categoryKey = isLocalized.categories[category] ? isLocalized.categories[category].key : category;
						return ({ ...acc, [categoryKey]: [...rawCategories[category]] });
					}, {}) : rawCategories;

					if ((newProducts || productsOnSale) && categories[level1URI]) {
						const categoryItems = categories.archive ? difference(categories[level1URI], categories.archive) : categories[level1URI];
						if (newProducts && intersect(categoryItems, newProducts).length > 0) hasCampaigns.newProducts = 1;
						if (productsOnSale && intersect(categoryItems, productsOnSale).length > 0) hasCampaigns.productsOnSale = 1;
					}

					return (
						<li className={[s.level1_column, ...(navigation.expandedMenuItem === i ? [s.level1_column__expanded] : [])].join(' ')} key={urlify(combinedURILevel1)} style={level1Styles}>
							<Link className={[s.level1_column_link, (combinedURILevel1.indexOf('sale') !== -1 || combinedURILevel1.indexOf('rea') !== -1 ? s.level1_column_link__sale : null)].join(' ')} to={combinedURILevel1} {...level1Click}>{expandable}<span className="a-level1Link">{translate(setup, level1, 'title')}</span></Link>
							{
								level1.subPages && (
									<div className={s.level1_columnInner}>
										<div className={[s.level2, (level2Links.length > 0 ? s.level2__minimal : null)].join(' ')}>
											{
												((level1.campaigns && Object.keys(hasCampaigns).length > 0) || level2Links.length > 0) && (
													<div className={s.level2_block}>
														{ level2Links.length > 0 && level2Links }
														{/*
															hasCampaigns.newProducts && (
																<div className={s.level2_block_item}>
																	<Link onClick={resetFilterHandler} className={[s.level2_block_item_link, 'a-level2LinkNew'].join(' ')} to={`${combinedURILevel1}/${translate(setup, 'filterNewUri')}`}>
																		<span className="a-level2LinkNew">{translate(setup, 'filterNew')}</span>
																	</Link>
																</div>
															)
														}
														{
															hasCampaigns.productsOnSale && (
																<div className={s.level2_block_item}>
																	<Link onClick={resetFilterHandler} className={[s.level2_block_item_link, s.level2_block_item_link__sale, 'a-level2LinkSale'].join(' ')} to={`${combinedURILevel1}/${translate(setup, 'filterSaleUri')}`}>
																		<span className="a-level2LinkSale">{translate(setup, 'filterSale')}</span>
																	</Link>
																</div>
															)
														*/}
													</div>
												)
											}
											{ level2SubCategoryLinks }
										</div>
									</div>
								)
							}
							{
								brands && isBrandsList && (
									<div className={s.level1_columnInner}>
										<div className={s.level2}>
											{
												brandColumns.map(({ letters, brands: brandItems }) => (
													<div key={letters.join('-')} className={s.level2_block}>
														<strong className={s.level2_block_title}>{`${letters.slice(0, 1)} — ${letters.slice(-1)}`}</strong>
														{
															brandItems.map((brand) => (
																<div key={translate(setup, brand.uri)} className={s.level2_block_item}>
																	<Link onClick={resetFilterHandler} className={[s.level2_block_item_link, 'a-level2Link'].join(' ')} to={`${combinedURILevel1}/${translate(setup, brand.uri)}`}><span className="a-level2Link">{ translate(setup, brand.title) }</span></Link>
																</div>
															))
														}
													</div>
												))
											}
										</div>
									</div>
								)
							}
						</li>
					);
				})
			}
		</ul>
	</nav>
);

export default hot(module)(Navigation);
